import type { FormEventHandler } from 'react'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { formToObject } from 'lib/forms/formToObject'
import TextButton from 'LuxKit/Button/TextButton'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import noop from 'lib/function/noop'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextInput from 'components/Common/Form/Input/TextInput'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { sendOTPVerificationCode, validateOTPVerificationCode } from 'actions/AuthActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { AccountAccessModalResult } from './AccountAccessModal.types'
import { trackClientEvent } from 'analytics/analytics'
import HelpPane from 'components/Common/HelpPane'
import ContactContext from 'contexts/contactContext'
import TextLink from 'LuxKit/TextLink'
import useInterval from 'hooks/useInterval'

interface LoginForm {
  email: string
  code: string
}

interface Props {
  user: App.User
  error?: any
  onModeChange?: (mode: App.UiAccountModalMode) => void
  dismissable?: boolean
}

function AccountAccessLoginOTP(props: Props) {
  const {
    user,
    onModeChange = noop,
    dismissable,
  } = props

  const intl = useIntl()
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const reRef = useRef<ReCAPTCHA>(null)
  const loggedIn = useAppSelector(selectLoggedIn)
  const dispatch = useAppDispatch()
  const error = useAppSelector(state => state.auth.error)
  const [countDown, setCountDown] = useState<number>(30)
  const [submitEnabled, setSubmitEnabled] = useState(true)

  useInterval(
    () => { setCountDown((curr) => Math.max(curr - 1, 0)) },
    1000,
    { disabled: countDown <= 0 },
  )

  const currentContact = useContext(ContactContext)
  const contactNumber = currentContact.defaultContact

  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    } else if (error) {
      setSubmitEnabled(true)
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, error])

  useEffect(() => {
    trackClientEvent({
      subject: 'sign_in_otp',
      action: 'impression',
      category: 'logging',
      type: 'operational',
    })
  }, [])

  const onResendCode = useCallback(() => {
    trackClientEvent({
      subject: 'sign_in_otp_resend',
      action: 'interaction',
      category: 'logging',
      type: 'operational',
    })
    setCountDown(30)
    dispatch(sendOTPVerificationCode(user))
  }, [dispatch, user])

  const onValidateCode = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    setSubmitEnabled(false)
    const loginData = formToObject<LoginForm>(event.currentTarget)
    dispatch(validateOTPVerificationCode(user, loginData.code))
  }, [dispatch, user])

  const onBack = useCallback(() => onModeChange('loginPasswordOTP'), [onModeChange])

  const verificationCodeTitle = intl.formatMessage({
    id: 'zdBkUU0UIw8AkPBTLjEi',
    defaultMessage: 'Verification code',
    description: 'Modal title for login OTP verification step dialog window',
  })

  const serverErrorText = intl.formatMessage({
    id: '5n82hheSoZxt8yTRWi4p',
    defaultMessage: 'Something went wrong. Please try logging in with your password instead.',
    description: 'Error message when OTP verification fails with server error',
  })

  // instructionsText uses FormattedMessage inline (rich text with <strong>)

  const codeLabel = intl.formatMessage({
    id: 'EWCDzKriSpvW5Y-DMffF',
    defaultMessage: '6-digit code',
    description: 'Form field label for OTP code input field during login',
  })

  const codePlaceholder = intl.formatMessage({
    id: '_8hCtXHgFdckcvz1D--c',
    defaultMessage: 'Enter 6-digit code',
    description: 'Placeholder for OTP code input during login when field is empty',
  })

  const codeRequiredError = intl.formatMessage({
    id: 'ydj4xCsGbzQLoX0Od3-8',
    defaultMessage: 'Code is required',
    description: 'Validation error when OTP code field is empty during login',
  })

  const codeInvalidError = intl.formatMessage({
    id: '93GllCprPh8ea081t75f',
    defaultMessage: 'Your code must be 6 digits long',
    description: 'Validation error when OTP code is not 6 digits during login',
  })

  const submitLabel = intl.formatMessage({
    id: 'nC8FmMNMRNbqFBBwdBTv',
    defaultMessage: 'Submit',
    description: 'Submit button text for OTP verification during login',
  })

  const didntReceiveText = intl.formatMessage({
    id: '1ck7CUE-sDA_fqo35aZj',
    defaultMessage: 'Didn’t receive the SMS?',
    description: 'Help text asking if user received the SMS during login OTP',
  })

  const resendCountdownLabel = intl.formatMessage({
    id: '7neFS5k8h26rNxhyiNQ9',
    defaultMessage: 'Resend code in {countDown}s',
    description: 'Countdown text before user can resend OTP code during login',
  }, { countDown })

  const resendLabel = intl.formatMessage({
    id: '2h9rbO2hH6WAt-ePo9X5',
    defaultMessage: 'Resend code',
    description: 'Hyperlink text to resend OTP code during login in the account access section',
  })

  return (<>
    <ModalHeader
      title={verificationCodeTitle}
      onBackButtonClick={onBack}
      dismissible={dismissable}
    />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={24} as="form" onSubmit={onValidateCode} name="loginForm">
          {error?.status === 500 && <MessageBanner kind="critical">{serverErrorText}</MessageBanner>}
          {error?.message && error.status !== 500 && <MessageBanner kind="critical">{error.message}</MessageBanner>}
          <BodyText variant="medium">
            <FormattedMessage
              id="Xv4jI5itjgVb3oh2M94I"
              defaultMessage="We’ve sent a 6-digit code to <strong>{maskedPhone}</strong>. The code is valid for 10 minutes and after that you will need to request a new one."
              description="Instructions text explaining OTP code was sent during login"
              values={{
                maskedPhone: user.maskedPhone,
                strong: (chunks) => <strong>{chunks}</strong>,
              }}
            />
          </BodyText>
          <TextInput
            type="text"
            required
            id="code"
            name="code"
            label={codeLabel}
            placeholder={codePlaceholder}
            requiredErrorMessage={codeRequiredError}
            minLength={6}
            maxLength={6}
            pattern="^[0-9]*$"
            invalidErrorMessage={codeInvalidError}
            inputMode="numeric"
          />
          {(!error || (!!error && error.status !== 500)) && (
            <Group direction="vertical" gap={16} horizontalAlign="center">
              <TextButton
                kind="primary"
                size="large"
                type="submit"
                fit="flex"
                disabled={!submitEnabled}
              >
                {submitLabel}
              </TextButton>
              <Group direction="horizontal" gap={8}>
                <BodyText variant="medium">{didntReceiveText}</BodyText>
                <TextLink size="medium" onClick={onResendCode} disabled={countDown > 0}>
                  {countDown > 0 ? resendCountdownLabel : resendLabel}
                </TextLink>
              </Group>
            </Group>
          )}
          {error?.status === 500 && (
            <HelpPane contactNumber={contactNumber} />
          )}
          {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </>
  )
}

export default AccountAccessLoginOTP
